import React, { useState, useEffect, useContext } from 'react';
import { userAxios, setupAxiosInterceptors } from '../userAxios';
import { UserContext } from './UserProvider';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;
export const VendorContext = React.createContext();

const VendorProvider = (props) => {
  const { token, handleInvalidToken } = useContext(UserContext);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState({});
  const [comments, setComments] = useState([]);
  const [tableFeedTypes, setTableFeedTypes] = useState([]);
  const [tableVendorTypes, setTableVendorTypes] = useState([]);
  const [modalFeedTypes, setModalFeedTypes] = useState([]);
  const [modalVendorTypes, setModalVendorTypes] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(undefined);
  const [selectedLocalVendor, setSelectedLocalVendor] = useState(undefined);
  const [vendorSearch, setVendorSearch] = useState('');
  const [callVendorForRefresh, setCallVendorForRefresh] = useState(false);
  const [vendorSearchResults, setVendorSearchResults] = useState([]);
  const [vendorName, setVendorName] = useState('New Vendor');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const [isVendorOpen, setIsVendorOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [ignoreVendorModalClicks, setIgnoreVendorModalClicks] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const toggleIgnoreVendorModalClicks = (value) => {
    setIgnoreVendorModalClicks(value);
  };

  const data = vendors.map((vendor) => ({
    id: vendor.rowKey,
    Vendor: vendor.licensee,
    'Vendor Code': vendor.vendorCode,
    Status: vendor.status ? 'Active' : 'Inactive',
    feedTypeString: vendor.feedTypeString,
    vendorTypeString: vendor.vendorTypeString,
    activeSites: vendor.activeSites,
    totalSites: vendor.totalSites,
    inactiveSites: vendor.inactiveSites,
    reciprocalAllowed: !!vendor.reciprocalAllowed,
    hasReciprocal: !!vendor.hasReciprocal,
    dualVendor: vendor.dualVendor,
    startDate: vendor.startDate,
    endDate: vendor.endDate,
    linkedGroup: vendor.linkedGroup,
    isPrimary: vendor.isPrimary,
    hasLink: vendor.hasLink,
    consumers: vendor.consumers,
    provider: vendor.provider,
    isConsumer: vendor.isConsumer,
    isProvider: vendor.isProvider,
  }));

  useEffect(() => {
    setupAxiosInterceptors(handleInvalidToken);
  }, [handleInvalidToken]);

  const getAllVendors = async (page) => {
    try {
      const response = await userAxios.get(`${API_URL}/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(() => [...response.data]);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const getVendor = async (rowKey) => {
    try {
      const response = await userAxios.get(`${API_URL}/vendors/${rowKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setVendor(response.data);
        return response.data;
      }
      throw new Error('Error retrieving vendor data');
    } catch (error) {
      console.error('Error fetching vendor:', error);
      return null;
    }
  };

  const createVendor = async (data) => {
    data.delayProfile = 0;
    try {
      const existingVendor = vendors.find(
        (vendor) => vendor.licensee === data.licensee
      );
      if (existingVendor) {
        alert('Vendor with the same licensee already exists');
        return null;
      }
      const response = await userAxios.post(`${API_URL}/vendors`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const createdVendorData = response.data;
      await getVendor(createdVendorData.rowKey);
      handleVendorClick(createdVendorData.rowKey);
      setVendor(createdVendorData);
      getAllVendors();
      return createdVendorData;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleVendorClick = async (id, e, shouldNavigate = true) => {
    if (e && e.target.type === 'checkbox') {
      return;
    }
    try {
      const vendor = await getVendor(id);
      setSelectedVendor(vendor);
      setVendorName(vendor.licensee);
      openModal();
      if (shouldNavigate) {
        navigate(`/vendors/${id}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateVendor = (rowKey, form) => {
    return userAxios
      .put(`${API_URL}/vendors/${rowKey}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        getAllVendors();
        return response.data;
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const archiveVendor = async (id) => {
    try {
      await userAxios.delete(`${API_URL}/vendors/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors((prevVendors) =>
        prevVendors.filter((vendor) => vendor.rowKey !== id)
      );
      closeVendorModal();
      setText('Vendor was archived');
      setShowSuccess(true);
    } catch (error) {
      console.error(error);
      setText('Vendor failed to archive');
      setShowError(true);
    }
  };

  const getVendorWebsites = async (vendorId) => {
    try {
      const response = await userAxios.get(
        `${API_URL}/vendors/${vendorId}/websites`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor website:', error);
    }
  };

  async function getVendors(vendorId = null) {
    try {
      const response = await userAxios.get(API_URL + '/vendors', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const vendors = response.data;
      setVendors(vendors);
      if (vendorId) {
        return vendors.find((x) => x.rowKey === Number(vendorId)).licensee;
      }
      return vendors;
    } catch (error) {
      console.error('Failed to fetch vendors', error);
      throw error;
    }
  }

  const getVendorComments = async (id) => {
    try {
      const response = await userAxios.get(`${API_URL}/vendor/${id}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComments(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getLinkedVendors = async (vendorId) => {
    try {
      const response = await userAxios.get(
        `${API_URL}/vendors/${vendorId}/linked`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching linked vendors:', error);
      return [];
    }
  };

  const linkVendors = (vendorId, form) => {
    return userAxios
      .put(`${API_URL}/vendors/${vendorId}/link`, form, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        getVendor(vendorId);
        return response.data;
      })
      .catch((error) => {
        console.error('Error during link operation:', error);
      });
  };

  const unLinkVendors = (vendorId, form) => {
    return userAxios
      .put(`${API_URL}/vendors/${vendorId}/unlink`, form, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        getVendor(vendorId);
        return response.data;
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const postComment = async (id, data) => {
    try {
      await userAxios.post(API_URL + '/vendor/' + id + '/comments', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      getVendorComments(id);
    } catch (error) {
      console.error(error);
    }
  };

  const deleteComment = async (vendorId, commentId) => {
    try {
      await userAxios.delete(
        `${API_URL}/vendor/${vendorId}/comments/${commentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      getVendorComments(vendorId);
    } catch (error) {
      console.error(error);
    }
  };

  const resetComments = () => {
    setComments([]);
  };

  const openModal = () => {
    setIsVendorOpen(true);
    setIsFormChanged(false);
  };

  const closeVendorModal = () => {
    setIsVendorOpen(false);
    setSelectedVendor(null);
    setComments([]);
    setIsFormChanged(false);
    navigate('/vendors', { replace: true });
  };

  const getTableVendorTypes = async () => {
    try {
      const response = await userAxios.get(API_URL + '/vendortypes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTableVendorTypes(response.data);
    } catch (error) {
      console.error('Error fetching vendor types:', error);
      return [];
    }
  };

  const getModalVendorTypes = async () => {
    try {
      const response = await userAxios.get(API_URL + '/vendortypes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const modalVendorTypes = response.data;
      setModalVendorTypes(modalVendorTypes);
      return modalVendorTypes;
    } catch (error) {
      console.error('Error fetching vendor types:', error);
      return [];
    }
  };

  const getTableFeedTypes = async () => {
    try {
      const response = await userAxios.get(API_URL + '/feedtypes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTableFeedTypes(response.data);
      return tableFeedTypes;
    } catch (error) {
      console.error('Error fetching feed types:', error);
      return [];
    }
  };

  const getModalFeedTypes = async () => {
    try {
      const response = await userAxios.get(API_URL + '/feedtypes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const feedTypes = response.data;
      setModalFeedTypes(feedTypes);
      return feedTypes;
    } catch (error) {
      console.error('Error fetching feed types:', error);
      return [];
    }
  };

  const nextVendor = () => {
    const currentIndex = vendors.findIndex(
      (v) => v.rowKey === selectedVendor.rowKey
    );
    if (currentIndex < vendors.length - 1) {
      const nextVendor = vendors[currentIndex + 1];
      handleVendorClick(nextVendor.rowKey);
    }
  };

  const previousVendor = () => {
    const currentIndex = vendors.findIndex(
      (v) => v.rowKey === selectedVendor.rowKey
    );
    if (currentIndex > 0) {
      const prevVendor = vendors[currentIndex - 1];
      handleVendorClick(prevVendor.rowKey);
    }
  };

  const importVendors = async (csvContent) => {
    try {
      const base64Content = btoa(csvContent);
      const payload = {
        type: 'vendor',
        data: base64Content
      };
      const response = await userAxios.post(`${API_URL}/import`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setText('Data imported successfully');
      setShowSuccess(true);
      return response.data;
    } catch (error) {
      console.error('Error importing vendors:', error);
      setText('Error importing data');
      setShowError(true);
      throw error;
    }
  };

  const vendorReciprocalAllowed = selectedVendor
    ? selectedVendor.reciprocalAllowed
    : undefined;

  return (
    <VendorContext.Provider
      value={{
        vendors,
        vendor,
        comments,
        setComments,
        modalFeedTypes,
        modalVendorTypes,
        tableFeedTypes,
        setTableFeedTypes,
        tableVendorTypes,
        setTableVendorTypes,
        isFormChanged,
        setIsFormChanged,
        getTableFeedTypes,
        getTableVendorTypes,
        getModalVendorTypes,
        getModalFeedTypes,
        isOpen,
        setIsOpen,
        isVendorOpen,
        setIsVendorOpen,
        data,
        selectedVendor,
        setSelectedVendor,
        vendorName,
        setVendorName,
        getLinkedVendors,
        linkVendors,
        unLinkVendors,
        getAllVendors,
        getVendor,
        createVendor,
        updateVendor,
        archiveVendor,
        getVendorWebsites,
        getVendors,
        getVendorComments,
        postComment,
        deleteComment,
        resetComments,
        handleVendorClick,
        openModal,
        closeVendorModal,
        nextVendor,
        previousVendor,
        vendorSearch,
        setVendorSearch,
        vendorSearchResults,
        setVendorSearchResults,
        ignoreVendorModalClicks,
        toggleIgnoreVendorModalClicks,
        showSuccess,
        setShowSuccess,
        showError,
        setShowError,
        text,
        setText,
        selectedLocalVendor,
        setSelectedLocalVendor,
        callVendorForRefresh,
        setCallVendorForRefresh,
        vendorReciprocalAllowed,
        importVendors
      }}
    >
      {props.children}
    </VendorContext.Provider>
  );
};

export default VendorProvider;